import { Select } from "antd";
import { useState } from "react";
import { useIntl } from "react-intl";
import { DataTableImageColumn } from "~components/DataTable/components/DataTableImageColumn/DataTableImageColumn.tsx";
import type { DataTableColumn } from "~components/DataTable/types/dataTableColumn.ts";
import type { InfinitySelectOption } from "~components/InfinitySelect";
import { SelectConference } from "~components/SelectConference/SelectConference.tsx";
import { SelectDivision } from "~components/SelectDivision/SelectDivision.tsx";
import { EntityStatuses } from "~enums/entityStatuses.ts";
import { ImageTypes } from "~enums/imageTypes.ts";
import { SeasonEntityRosterStatuses } from "~enums/seasonEntityRosterStatuses.ts";
import { useUpsertSeasonEntity } from "~pages/SeasonEntities/hooks/useUpsertSeasonEntity";
import { SeasonEntityTableColumns } from "~pages/SeasonEntities/SeasonEntityListPage/enums/seasonEntityTableColumns.ts";
import type { SeasonEntityTransformed } from "~pages/SeasonEntities/types/seasonEntityTransformed";
import type { UpsertSeasonEntityModel } from "~pages/SeasonEntities/types/upsertSeasonEntityModel";

export const useGetSeasonEntityTableColumns = (massEditMode: boolean): Array<DataTableColumn<SeasonEntityTransformed>> => {
  const { formatMessage } = useIntl();
  const { mutateAsync: upsertSeasonEntity } = useUpsertSeasonEntity();
  const [updatingEntityId, setUpdatingEntityId] = useState<string | null>(null);

  const updateSeasonEntityField = async (row: SeasonEntityTransformed, upsertData: Omit<UpsertSeasonEntityModel, "entityId">) => {
    setUpdatingEntityId(row.entityId);

    try {
      await upsertSeasonEntity({
        entityId: row.entityId,
        ...upsertData,
      });
    } finally {
      setUpdatingEntityId(null);
    }
  };

  const isRowUpdating = (entityId: string) => updatingEntityId === entityId;

  return [
    {
      key: SeasonEntityTableColumns.Image,
      title: formatMessage({ id: "entities.table.column.image" }),
      dataIndex: ["entity", "images"],
      width: 50,
      render: (_, row) => <DataTableImageColumn images={row.entity?.images || []} imageTypes={[ImageTypes.Logo]} />,
    },
    {
      key: SeasonEntityTableColumns.NameFullLocal,
      title: formatMessage({ id: "name.local.full" }),
      dataIndex: ["entity", "nameFullLocal"],
      sorter: true,
      width: 240,
      ellipsis: { showTitle: true },
    },
    {
      key: SeasonEntityTableColumns.NameFullLatin,
      title: formatMessage({ id: "name.latin.full" }),
      dataIndex: ["entity", "nameFullLatin"],
      width: 240,
      ellipsis: { showTitle: true },
    },
    {
      key: SeasonEntityTableColumns.CodeLocal,
      title: formatMessage({ id: "name.local.code" }),
      dataIndex: ["entity", "codeLocal"],
      width: 120,
    },
    {
      key: SeasonEntityTableColumns.CodeLatin,
      title: formatMessage({ id: "name.latin.code" }),
      dataIndex: ["entity", "codeLatin"],
      width: 120,
    },
    {
      key: SeasonEntityTableColumns.EntityId,
      title: formatMessage({ id: "entity.id" }),
      dataIndex: "entityId",
      copyable: false,
      width: 260,
      sorter: true,
    },
    {
      key: SeasonEntityTableColumns.ExternalId,
      title: formatMessage({ id: "externalId" }),
      dataIndex: ["entity", "externalId"],
      copyable: false,
      width: 180,
      sorter: true,
    },
    {
      key: SeasonEntityTableColumns.Gender,
      title: formatMessage({ id: "gender" }),
      dataIndex: ["entity", "gender"],
      width: 120,
      sorter: true,
    },
    {
      key: SeasonEntityTableColumns.AgeGroup,
      title: formatMessage({ id: "ageGroup" }),
      dataIndex: ["entity", "ageGroup"],
      width: 140,
      sorter: true,
    },
    {
      key: SeasonEntityTableColumns.Standard,
      title: formatMessage({ id: "standard" }),
      dataIndex: ["entity", "standard"],
      width: 120,
      sorter: true,
    },
    {
      key: SeasonEntityTableColumns.Discipline,
      title: formatMessage({ id: "discipline" }),
      dataIndex: ["entity", "discipline"],
      width: 140,
      sorter: true,
    },
    {
      key: SeasonEntityTableColumns.EntityStatus,
      title: formatMessage({ id: "entity.status" }),
      dataIndex: ["entity", "status"],
      width: 140,
    },
    {
      key: SeasonEntityTableColumns.Status,
      title: formatMessage({ id: "status" }),
      dataIndex: "status",
      sorter: true,
      width: 120,
      render: (value: string, row) =>
        massEditMode ? (
          <Select
            disabled={isRowUpdating(row.entityId)}
            onChange={(status) => void updateSeasonEntityField(row, { status })}
            options={Object.values(EntityStatuses).map((status) => ({ label: status, value: status }))}
            style={{ width: "100%" }}
            value={value}
          />
        ) : (
          value
        ),
    },
    {
      key: SeasonEntityTableColumns.ConferenceName,
      title: formatMessage({ id: "conference" }),
      dataIndex: ["conference", "nameLocal"],
      width: 180,
      ellipsis: { showTitle: true },
      render: (_: string, row) =>
        massEditMode ? (
          <SelectConference
            disabled={isRowUpdating(row.entityId)}
            onChange={(conferenceOption: InfinitySelectOption | null) =>
              void updateSeasonEntityField(row, {
                conferenceId: conferenceOption?.value ?? null,
                divisionId: null,
              })
            }
            value={row.conference ? { label: row.conference.nameLocal, value: row.conference.conferenceId } : null}
          />
        ) : (
          row.conference?.nameLocal
        ),
    },
    {
      key: SeasonEntityTableColumns.DivisionName,
      title: formatMessage({ id: "division" }),
      dataIndex: ["division", "nameLocal"],
      width: 180,
      ellipsis: { showTitle: true },
      render: (_: string, row) =>
        massEditMode ? (
          <SelectDivision
            conferenceId={row.conference?.conferenceId}
            disabled={isRowUpdating(row.entityId) || !row.conference?.conferenceId}
            onChange={(divisionOption: InfinitySelectOption | null) =>
              void updateSeasonEntityField(row, { divisionId: divisionOption?.value ?? null })
            }
            value={row.division ? { label: row.division.nameLocal, value: row.division.divisionId } : null}
          />
        ) : (
          row.division?.nameLocal
        ),
    },
    {
      key: SeasonEntityTableColumns.RosterStatus,
      title: formatMessage({ id: "rosterStatus" }),
      dataIndex: "rosterStatus",
      width: 160,
      render: (value: string, row) =>
        massEditMode ? (
          <Select
            disabled={isRowUpdating(row.entityId)}
            onChange={(rosterStatus) => void updateSeasonEntityField(row, { rosterStatus })}
            options={Object.values(SeasonEntityRosterStatuses).map((rosterStatus) => ({ label: rosterStatus, value: rosterStatus }))}
            style={{ width: "100%" }}
            value={value}
          />
        ) : (
          value
        ),
    },
    {
      key: SeasonEntityTableColumns.Seed,
      title: formatMessage({ id: "seed" }),
      dataIndex: "seed",
      width: 100,
    },
  ];
};
